import { AlertTriangleIcon, CheckCircle2Icon, XIcon } from 'lucide-react';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  message: string;
  kind: ToastKind;
  /** Optional secondary action (e.g., Undo). Renders as a button next to dismiss. */
  action?: ToastAction;
}

interface ToastBannerProps {
  toast: Toast;
  onDismiss: () => void;
}

export function ToastBanner({ toast, onDismiss }: ToastBannerProps) {
  const borders = {
    success: 'border-emerald-800/60',
    error: 'border-rose-800/60',
    info: 'border-blue-800/60',
    warning: 'border-amber-700/60',
  };
  const iconClasses = {
    success: 'text-emerald-400',
    error: 'text-rose-400',
    info: 'text-blue-400',
    warning: 'text-amber-400',
  };
  const Icon = toast.kind === 'error' || toast.kind === 'warning' ? AlertTriangleIcon : CheckCircle2Icon;

  return (
    <div
      role="status"
      onKeyDown={event => event.stopPropagation()}
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-navy-light border ${borders[toast.kind]} rounded-lg px-4 py-3 shadow-xl max-w-[min(28rem,calc(100vw-3rem))]`}
    >
      <Icon aria-hidden="true" className={`w-5 h-5 ${iconClasses[toast.kind]} flex-shrink-0`} />
      <div
        role="region"
        aria-label="Notification message"
        tabIndex={0}
        className="min-w-0 max-h-[calc(100dvh-6rem)] overflow-y-auto [overflow-wrap:anywhere] text-sm text-white leading-tight focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-amber"
      >
        {toast.message}
      </div>
      {toast.action && (
        <button
          onClick={() => {
            toast.action!.onClick();
            onDismiss();
          }}
          className="ml-2 px-2 py-0.5 text-sm font-medium text-amber hover:text-amber-hover border border-amber/40 hover:border-amber/70 rounded flex-shrink-0"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={onDismiss}
        className="ml-1 p-1 text-gray-400 hover:text-white flex-shrink-0"
        aria-label="Dismiss"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
